import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  updateSircreb,
  sircrebPercent,
  type ParametroItem,
} from '@/shared/api/controllers/parametros';

/**
 * SIRCREB updater. Accepts the current parameter row, pre-fills its
 * percentage, and on save patches `Valor_P` and `FechaActualizacion_P`
 * via the controller. Matches the PowerApps "Actualizar SIRCREB" popup:
 * single numeric input, no history trail.
 */
export function SircrebUpdateDialog({
  item,
  open,
  onOpenChange,
}: {
  item: ParametroItem | null;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const currentPercent = sircrebPercent(item);
  const [value, setValue] = useState(() => formatPercentInput(currentPercent));

  // Re-seed the input whenever the dialog opens with a different parameter
  // snapshot (e.g. after a background refetch).
  useEffect(() => {
    if (open) setValue(formatPercentInput(currentPercent));
  }, [open, currentPercent]);

  const mutation = useMutation({
    mutationFn: async (percent: number) => {
      if (!item) throw new Error('Parámetro SIRCREB no disponible.');
      return updateSircreb(item.id, percent);
    },
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['home', 'sircreb'] });
      queryClient.invalidateQueries({ queryKey: ['tesoreria'] });
      toast.success('SIRCREB actualizado.', {
        description:
          r.rowsBackfilled > 0
            ? `${r.rowsBackfilled} movimiento${r.rowsBackfilled === 1 ? '' : 's'} recalculado${r.rowsBackfilled === 1 ? '' : 's'}.`
            : undefined,
      });
      onOpenChange(false);
    },
    onError: (err) => {
      toast.error('No se pudo actualizar el SIRCREB.', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    },
  });

  const parsed = parsePercentInput(value);
  // AFIP publishes SIRCREB at 2-decimal precision (e.g. 0.83%, 1.20%).
  // Round here so trailing input noise like "0.834567" doesn't drift
  // every downstream Math.round(monto * rate * 100) / 100 by a fraction
  // of a centavo per row.
  const rounded = parsed != null ? Math.round(parsed * 100) / 100 : null;
  const canSave = rounded != null && rounded >= 0 && !mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(next) => !mutation.isPending && onOpenChange(next)}>
      <DialogContent
        className="sm:max-w-md"
        processing={mutation.isPending && 'Actualizando SIRCREB…'}
      >
        <DialogHeader>
          <DialogTitle>Actualizar Valor SIRCREB</DialogTitle>
          <DialogDescription>
            Ingresá el nuevo valor del impuesto como porcentaje (por ejemplo{' '}
            <span className="font-medium">0,8</span> para 0,8%). Se redondea
            a 2 decimales.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSave && rounded != null) mutation.mutate(rounded);
          }}
          className="flex flex-1 flex-col overflow-hidden"
          noValidate
        >
          <DialogBody className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="sircreb-value">Valor (%)</Label>
              <div className="relative">
                <Input
                  id="sircreb-value"
                  type="text"
                  inputMode="decimal"
                  variant="filled"
                  autoFocus
                  value={value}
                  onChange={(e) => setValue(sanitizeDecimal(e.target.value))}
                  className="pr-10 text-right"
                />
                <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  %
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Valor actual:{' '}
                <span className="font-medium text-foreground">
                  {currentPercent != null ? `${formatPercentDisplay(currentPercent)}%` : '—'}
                </span>
                {item?.fields.FechaActualizacion_P && (
                  <> · Última actualización: {item.fields.FechaActualizacion_P}</>
                )}
              </p>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={!canSave}>
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function sanitizeDecimal(value: string): string {
  const cleaned = value.replace(/[^\d.,]/g, '');
  const match = cleaned.match(/^\d*([.,]\d*)?/);
  return match ? match[0] : '';
}

function parsePercentInput(value: string): number | null {
  if (!value.trim()) return null;
  const n = Number(value.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function formatPercentInput(percent: number | null): string {
  if (percent == null) return '';
  return String(percent).replace('.', ',');
}

function formatPercentDisplay(percent: number): string {
  return percent.toLocaleString('es-AR', { maximumFractionDigits: 4 });
}
